import React, { useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, Typography, Box, IconButton, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import LanguageIcon from '@material-ui/icons/Language';
import { generateBookmarkletCode } from './cloudService';
import BookmarkletHowTo from './BookmarkletHowTo';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: 500,
      width: '90%',
      borderRadius: 12,
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: '#1e3a5f',
    fontWeight: 600,
  },
  content: {
    padding: theme.spacing(3),
  },
  dragArea: {
    background: '#f8fafc',
    border: '2px dashed #cbd5e1',
    borderRadius: 8,
    padding: theme.spacing(3),
    textAlign: 'center',
    margin: theme.spacing(2, 0),
  },
  dragHint: {
    color: '#64748b',
    fontSize: '0.85rem',
    marginBottom: theme.spacing(1.5),
  },
  bookmarkletButton: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #3ecf8e 0%, #22c55e 100%)',
    color: 'white',
    padding: '12px 24px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    cursor: 'grab',
    boxShadow: '0 4px 12px rgba(62, 207, 142, 0.3)',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(62, 207, 142, 0.4)',
    },
  },
  note: {
    background: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: 6,
    padding: theme.spacing(1.5),
    fontSize: '0.85rem',
    color: '#92400e',
    marginTop: theme.spacing(2),
  },
  closeButton: {
    marginTop: theme.spacing(2),
    textAlign: 'right',
  },
}));

interface BookmarkletModalProps {
  open: boolean;
  onClose: () => void;
  schema?: string;
  moduleId?: string;
}

const BookmarkletModal: React.FC<BookmarkletModalProps> = ({ open, onClose, schema, moduleId }) => {
  const classes = useStyles();
  const bookmarkletCode = useMemo(() => generateBookmarkletCode(schema, moduleId), [schema, moduleId]);
  const bookmarkletRef = useRef<HTMLAnchorElement>(null);

  // Set the href via ref to avoid React's javascript: URL warning
  useEffect(() => {
    if (bookmarkletRef.current) {
      bookmarkletRef.current.setAttribute('href', bookmarkletCode);
    }
  }, [bookmarkletCode, open]);

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          <LanguageIcon />
          Preview Module on Your Website
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent className={classes.content}>
        <Typography variant="body2" color="textSecondary">
          Use this bookmarklet to preview your configured module on any webpage.
        </Typography>

        <Box className={classes.dragArea}>
          <Typography className={classes.dragHint}>Drag this up to your bookmarks bar:</Typography>
          <a
            ref={bookmarkletRef}
            href="#"
            className={classes.bookmarkletButton}
            onClick={(e) => e.preventDefault()}
            draggable="true"
            title="Drag this up to your bookmarks bar"
          >
            Quattr Module Preview
          </a>
        </Box>

        <BookmarkletHowTo bookmarkletCode={bookmarkletCode} enabled />

        <Box className={classes.note}>
          <strong>Note:</strong> This is a client-side preview only. The injected HTML won&apos;t persist after page
          refresh. Use this to validate placement and styling before implementation.
        </Box>

        <Box className={classes.closeButton}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BookmarkletModal;
